/** Small, dependency-free HTML helpers for the import layers. No DOM: these run in Vitest under Node too. */

const NAMED: Record<string, string> = {
  amp: "&", lt: "<", gt: ">", quot: '"', apos: "'", nbsp: " ", ndash: "–", mdash: "—",
  hellip: "…", rsquo: "’", lsquo: "‘", rdquo: "”", ldquo: "“", eacute: "é",
};

export function decodeEntities(s: string): string {
  return s.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (m, body: string) => {
    if (body[0] === "#") {
      const code = body[1]?.toLowerCase() === "x" ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10);
      return Number.isFinite(code) ? String.fromCodePoint(code) : m;
    }
    return NAMED[body.toLowerCase()] ?? m;
  });
}

/** All <meta ...> tags as attribute maps (lower-cased attribute names, entity-decoded values). */
export function metaTags(html: string): Record<string, string>[] {
  const out: Record<string, string>[] = [];
  for (const tag of html.match(/<meta\b[^>]*>/gi) ?? []) {
    const attrs: Record<string, string> = {};
    for (const m of tag.matchAll(/([a-zA-Z_:][\w:.-]*)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>]+))/g)) {
      attrs[(m[1] as string).toLowerCase()] = decodeEntities(m[2] ?? m[3] ?? m[4] ?? "");
    }
    out.push(attrs);
  }
  return out;
}

/** Contents of every <script type="application/ld+json"> block. */
export function jsonLdBlocks(html: string): string[] {
  const out: string[] = [];
  for (const m of html.matchAll(/<script\b[^>]*type\s*=\s*["']application\/ld\+json["'][^>]*>([\s\S]*?)<\/script>/gi)) {
    out.push((m[1] as string).trim());
  }
  return out;
}

/** Strip tags and collapse whitespace; good enough to feed description text to the LLM layer. */
export function textOf(html: string): string {
  return decodeEntities(
    html.replace(/<(script|style)\b[\s\S]*?<\/\1>/gi, " ")
        .replace(/<br\s*\/?>|<\/p>|<\/li>|<\/h\d>/gi, "\n")
        .replace(/<[^>]+>/g, " "),
  ).replace(/[ \t ]+/g, " ").replace(/\s*\n\s*/g, "\n").trim();
}

export function origin(url: string): string | null {
  try {
    const u = new URL(url);
    return `${u.protocol}//${u.host}`;
  } catch {
    return null;
  }
}
